import React, { useMemo, useState } from "react";
import { Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select } from "@material-ui/core";
import { AllSponsors, AllTemplates, ProductPersonalBibSettings, ProductType, Sponsor, Template } from "../../../model/Product";
import useDidMountEffect from "../../Common/UseDidMountEffect";
import { getInititalBibSettingsValue } from "./utils";
import { AllEntryPersonalBibHandoutLocations, EntryPersonalBibHandoutLocation } from "../../../model/CommonTypes";
import ProductList from "../../Common/ProductList/ProductList";
import { ProductListChangeEvent } from "../../Common/ProductList/utils";
import VasaloppetFieldset from "../../Common/VasaloppetFieldset";
import { Alert } from "@material-ui/lab";

interface IProps {
    productId: string;
    productType: ProductType;
    defaultValue?: ProductPersonalBibSettings;
    onChange: (nextValue: ProductPersonalBibSettings) => void;
}

type CheckboxProperty = Pick<ProductPersonalBibSettings, "canBeBought" | "handoutLocationCanBeChanged" | "isArchived">;

const EditProductBibSettingsCard = (props: IProps) => {
    const [value, setValue] = useState(getInititalBibSettingsValue(props.productId, props.defaultValue));

    useDidMountEffect(() => {
        // When productType changes -> Reset all controls to default state
        setValue(getInititalBibSettingsValue(props.productId));
    }, [props.productType]);

    useDidMountEffect(() => {
        if (props.productType !== "race") {
            props.onChange(null);
            return;
        }

        props.onChange(value);
    }, [value]);

    const hasErrors = useMemo(() => {
        if (!value) {
            return true;
        }

        return !(value.bibProductId && value.handoutLocations?.length > 0 && value.sponsors?.length > 0);
    }, [value]);

    const handleCbxChange = (prop: keyof CheckboxProperty) => (ev: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = {
            ...value,
            [prop]: ev.target.checked
        };
        setValue(nextValue);
    };

    const handleBibProductChange = (productId: string) => {
        const nextValue = {
            ...value,
            bibProductId: productId
        };
        setValue(nextValue);
    };

    const handleSponsorsChange = (sponsors: Sponsor[]) => {
        const nextValue = {
            ...value,
            sponsors: sponsors
        };
        setValue(nextValue);
    };

    const handleHandoutLocationChange = (handoutLocations: EntryPersonalBibHandoutLocation[]) => {
        const nextValue = {
            ...value,
            handoutLocations: handoutLocations
        };
        setValue(nextValue);
    };

    const handleTemplateChange = (template: Template) => {
        const nextValue = {
            ...value,
            template: template
        };
        setValue(nextValue);
    };

    const render = () => {
        if (props.productType !== "race") {
            return null;
        }

        const bibProductIdNotSet = !value.bibProductId;

        return (
            <Grid container spacing={2} style={{ width: "100%" }}>
                <Grid item xs={12}>
                    {hasErrors &&
                        <Alert severity="warning">Produkt, Sponsorer och Utlämningsställen måste anges</Alert>
                    }
                </Grid>
                <Grid item xs={3}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Produkt</InputLabel>
                        <ProductList
                            initialValue={value.bibProductId}
                            onChange={async (evt: ProductListChangeEvent) => {
                                handleBibProductChange(evt.selectedIds[0])
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormControl fullWidth disabled={bibProductIdNotSet}>
                        <InputLabel shrink>Sponsorer</InputLabel>
                        <Select multiple
                            value={value.sponsors}
                            onChange={(evt) => {
                                handleSponsorsChange(evt.target.value as Sponsor[]);
                            }}
                        >
                            {AllSponsors.map((item) => {
                                return <MenuItem key={item} value={item}>{item}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormControl fullWidth disabled={bibProductIdNotSet}>
                        <InputLabel shrink>Utlämningsställen</InputLabel>
                        <Select multiple
                            value={value.handoutLocations}
                            onChange={(evt) => {
                                handleHandoutLocationChange(evt.target.value as EntryPersonalBibHandoutLocation[]);
                            }}
                        >
                            {AllEntryPersonalBibHandoutLocations.map((item) => {
                                return <MenuItem key={item} value={item}>{item}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormControl fullWidth disabled={bibProductIdNotSet}>
                        <InputLabel shrink>Mall</InputLabel>
                        <Select
                            value={value.template}
                            onChange={(evt) => {
                                handleTemplateChange(evt.target.value as Template);
                            }}
                        >
                            {AllTemplates.map((item) => {
                                return <MenuItem key={item} value={item}>{item}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <VasaloppetFieldset title="Inställningar">
                        <FormControlLabel label="Personlig nummerlapp kan köpas"
                            disabled={bibProductIdNotSet}
                            control={
                                <Checkbox color="primary"
                                    checked={value.canBeBought}
                                    onChange={handleCbxChange("canBeBought")}
                                />
                            } />
                        <FormControlLabel label="Utlämningsställe kan ändras"
                            disabled={bibProductIdNotSet}
                            control={
                                <Checkbox color="primary"
                                    checked={value.handoutLocationCanBeChanged}
                                    onChange={handleCbxChange("handoutLocationCanBeChanged")}
                                />
                            } />
                        <FormControlLabel label="Arkiverad"
                            disabled={bibProductIdNotSet}
                            control={
                                <Checkbox color="primary"
                                    checked={value.isArchived}
                                    onChange={handleCbxChange("isArchived")}
                                />
                            } />
                    </VasaloppetFieldset>
                </Grid>
            </Grid>
        );
    };

    return render();
};

export default EditProductBibSettingsCard;
